import { matchPath } from "react-router";
import { REPORT_QUERY_PARAMS } from "@sscs/report/constants";
import { ReportRequest, ReportSummaryRequest } from "@sscs/report/report";
import {
  ReportSummaryData,
  SDLCReportSummary,
  SoftwareInfo,
} from "@sscs/types";
import { FULL_REPORT_PATH, REPORT_TYPE_REPRO } from "Constants";
import type { AppDispatch, RootState } from "Store/store";
import {
  FilestreamFileMessage,
  GroupUpdatesMessage,
  ProcessingStatus,
  VersionMessage,
} from "Types";
import {
  generateReportKey,
  getRouterCompatiblePathname,
  markReportAsDeleted,
} from "Utils";

type ReportInfo = {
  current_status: ProcessingStatus;
  summary: SDLCReportSummary | null;
  software_info: SoftwareInfo;
  diffId: string | null;
};

const getUpdatedReport = (
  message: FilestreamFileMessage | VersionMessage,
  file_id: string,
  isRepro: boolean,
): ReportInfo | undefined => {
  if (message.entity === "filestream_file") {
    const updatedFile =
      message.action !== "deleted"
        ? message.data.find((file) => file.id === file_id)
        : undefined;

    if (updatedFile)
      return {
        current_status: updatedFile.current_status,
        summary: updatedFile.latest_report_summary,
        software_info: updatedFile.software_info,
        diffId: null,
      };
    else return undefined;
  } else {
    const updatedVersion =
      message.action !== "deleted"
        ? message.data.find((version) => version.version_id === file_id)
        : undefined;

    if (updatedVersion && updatedVersion.artifacts_summary) {
      const artifactSummary = isRepro
        ? updatedVersion.artifacts_summary.repro
        : updatedVersion.artifacts_summary.version;

      if (!artifactSummary) return undefined;

      return {
        current_status: artifactSummary.current_status,
        summary: artifactSummary.latest_report_summary,
        software_info: updatedVersion.software_info,
        diffId: artifactSummary.latest_report_summary?.diff_report_id ?? null,
      };
    } else return undefined;
  }
};

export const updateSummaryApiCache = (
  dispatch: AppDispatch,
  state: RootState,
  draft: ReportSummaryData,
  message: GroupUpdatesMessage,
  arg: ReportRequest<ReportSummaryRequest>,
): ReportSummaryData => {
  const params = new URLSearchParams(document.location.search);
  const build = params.get(REPORT_QUERY_PARAMS.build);
  const diffWith = params.get(REPORT_QUERY_PARAMS.diff_with);
  const isRepro = build === REPORT_TYPE_REPRO;
  const match = matchPath(
    { path: FULL_REPORT_PATH, end: false },
    getRouterCompatiblePathname(),
  );
  const { groupId, projectName, packageName, version } = match?.params ?? {};
  const openedReportKey = generateReportKey({
    groupId: groupId ?? "",
    projectName: projectName ?? "",
    packageName: packageName ?? "",
    version: version ?? "",
    build: build,
    diffWith: diffWith ?? undefined,
  });

  if (message.entity === "filestream_file" || message.entity === "version") {
    if (!message.keys.some((id) => id === draft.file_id)) {
      return draft;
    }

    if (message.action === "updated") {
      const updatedReport = getUpdatedReport(message, draft.file_id, isRepro);

      if (!updatedReport) return draft;

      if (
        !updatedReport.summary ||
        updatedReport.current_status.code !== "DONE"
      )
        return { ...draft, software_info: updatedReport.software_info };

      return {
        ...draft,
        is_outdated:
          updatedReport.summary.is_outdated ||
          updatedReport.summary.report_id !== arg.ui_id,
        outdated_reason: updatedReport.summary.is_outdated
          ? updatedReport.summary.outdated_reason
          : draft.outdated_reason,
        is_latest: updatedReport.summary.report_id === arg.ui_id,
        software_info: updatedReport.software_info,
      };
    } else if (message.action === "deleted") {
      dispatch(markReportAsDeleted(openedReportKey));
    }
  } else if (message.entity === "project") {
    if (
      message.action === "deleted" &&
      message.keys.some(
        (deletedProjectId) => deletedProjectId === draft.project_id,
      )
    ) {
      dispatch(markReportAsDeleted(openedReportKey));
    }
  } else if (message.entity === "package") {
    if (
      message.action === "deleted" &&
      message.keys.some(
        (deletedPackageId) => deletedPackageId === draft.package_id,
      )
    ) {
      dispatch(markReportAsDeleted(openedReportKey));
    }
  }

  return draft;
};
